import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import styles from './CreateOrderModal.module.scss';
import { Button } from 'ui/Button';
import { Label } from 'components/CreateOrderForm';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ReactComponent as ArrowSvg } from 'assets/img/filter/arrow.svg';
import {
  Group,
  NumberInput,
  Select,
  Textarea,
  TextInput,
  Switch,
  Stack,
  Loader,
  NumberInputHandlers,
} from '@mantine/core';
import {
  useCreateOrderTemplateMutation,
  useDeleteOrderTemplateMutation,
  useGetOrderTemplatesFiltersQuery,
  useGetTypesFiltersQuery,
  useGetUnitMeasuresQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { DateInput } from '@mantine/dates';
import moment from 'moment';
import { ReactComponent as CalendarSvg } from 'assets/img/filter/iconCalendarBig.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import iconDelete from './img/iconDeleteGray.svg';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import { normalizeUnitPrice } from 'utils/normalizeUnitPrice';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { ModalConfirmTemplate } from '../OrderTemplates/ModalConfirmTemplate';
import { AddressDadata3 } from 'components/AddressDadata3';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { InitialTemplateType } from '../OrderTemplates';
import {
  isServiceTypeArray,
  isTypesOfServicesResponse,
} from 'components/CreateOrderForm/types';
import { useDebouncedValue } from '@mantine/hooks';
import { ReactComponent as IconChewronDown } from './img/iconChevronDown.svg';
import { ReactComponent as IconChewronUp } from './img/iconChevronUp.svg';
import ContentLoader from 'react-content-loader';
import { AppTooltip } from 'ui/AppTooltip';

type CreateOrderFormValues = {
  is_unit_based: boolean;
  template_name: string;
  order_type_id: string | number | null;
  order_type: string;
  unit_price: number | string | null | undefined;
  unit_volume: number | string | null | undefined;
  amount: number | string | null | undefined;
  description: string;
  accept_automatically: boolean | number;
  add_tax_amount: boolean;
  date_start: Date | null;
  date_end: Date | null;
  is_remote: boolean;
  city: string;
  street: string;
  home: string;
  apartment: string;
  use_fast_withdraw: boolean;
  unit_measure: string;
  total_price: number | string | null | undefined;
  unit_volume_min: number | string | null | undefined;
  unit_volume_max: number | string | null | undefined;
};

const getInitialValues = (defaultFastWithdraw = false): CreateOrderFormValues => ({
  is_unit_based: false,
  unit_price: null,
  unit_volume: 1,
  template_name: '',
  order_type_id: '',
  order_type: '',
  amount: null,
  description: '',
  accept_automatically: false,
  add_tax_amount: false,
  date_start: null,
  date_end: null,
  is_remote: true,
  city: '',
  street: '',
  home: '',
  apartment: '',
  use_fast_withdraw: defaultFastWithdraw,
  unit_measure: '',
  total_price: null,
  unit_volume_min: null,
  unit_volume_max: null,
});

const QuantityStepperControls: React.FC<{
  disabled?: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  decrementDisabled?: boolean;
}> = ({ onIncrement, onDecrement, decrementDisabled, disabled }) => (
  <div className={styles.quantityControls}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
      }}>
      <button
        type='button'
        className={styles.quantityControlBtn}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onIncrement}
        disabled={disabled}
        aria-label='Увеличить объем'>
        <IconChewronUp />
      </button>
    </div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
      }}>
      <button
        type='button'
        className={styles.quantityControlBtn}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onDecrement}
        disabled={decrementDisabled || disabled}
        aria-label='Уменьшить объем'>
        <IconChewronDown />
      </button>
    </div>
  </div>
);

export const CreateOrderModal = ({
  setOpen,
  initialData,
  currentId,
  onSuccess,
}: {
  setOpen: (open: boolean) => void;
  initialData?: InitialTemplateType | null;
  currentId?: number | null;
  onSuccess?: (message: string) => void;
}) => {
  const [templateName, setTemplateName] = useState('');
  const trimmedTemplateName = templateName.trim();
  const [debouncedTemplateNameCheck] = useDebouncedValue(trimmedTemplateName, 300);
  const isTemplateNameCheckStale = debouncedTemplateNameCheck !== trimmedTemplateName;
  const { data: templatesCheckData, isFetching: isCheckingTemplateName } =
    useGetOrderTemplatesFiltersQuery(
      {
        page: 1,
        per_page: 20,
        search: debouncedTemplateNameCheck,
      },
      { skip: !trimmedTemplateName || !!currentId || isTemplateNameCheckStale },
    );
  const templateNameExists =
    !currentId &&
    !isTemplateNameCheckStale &&
    !isCheckingTemplateName &&
    Boolean(trimmedTemplateName) &&
    (templatesCheckData?.data || []).some(
      (template) => template.name === trimmedTemplateName,
    );
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: profileData } = useGetProfileQuery();

  const { infoPercent } = useSelector((state: RootState) => state.authSlice);
  const [createOrderTemplate, { isLoading: isLoadingCreate }] =
    useCreateOrderTemplateMutation();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [deleteOrderTemplate, { isLoading: isDeleting }] =
    useDeleteOrderTemplateMutation();
  const defaultFastWithdraw = profileData?.settings?.use_fast_withdraw || false;
  const [values, setValues] = useState<CreateOrderFormValues>(() => {
    if (!initialData) {
      return getInitialValues(defaultFastWithdraw);
    }

    return {
      ...getInitialValues(defaultFastWithdraw),
      ...initialData,
      template_name: initialData.template_name ?? initialData.name ?? '',
      unit_price: initialData.unit_price ?? initialData.amount ?? null,
      total_price:
        initialData?.unit_price && initialData?.unit_volume
          ? +initialData.unit_price * +initialData.unit_volume
          : null,
    } as CreateOrderFormValues;
  });
  const [typeSearchInput, setTypeSearchInput] = useState('');
  const [typeSearchQuery, setTypeSearchQuery] = useState('');
  const [debouncedTypeSearch] = useDebouncedValue(typeSearchQuery, 300);
  const [typesDropdownOpen, setTypesDropdownOpen] = useState(false);
  const [typesPage, setTypesPage] = useState(1);
  const [hasDescriptionScroll, setHasDescriptionScroll] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(0);
  const typesViewportRef = useRef<HTMLDivElement>(null);
  const typesLoadMoreLockRef = useRef(false);
  const suppressTypeSearchRef = useRef(false);
  const prevDebouncedTypeSearchRef = useRef(debouncedTypeSearch);
  const unitHandlersRef = useRef<NumberInputHandlers>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const descriptionResizeRef = useRef<HTMLDivElement>(null);
  const descriptionSelectionRef = useRef<{ start: number; end: number } | null>(
    null,
  );
  const { data: unitMeasuresData, isLoading: isLoadingUnitMeasures } =
    useGetUnitMeasuresQuery();

  const transformedUnitMeasuresData = useMemo(() => {
    if (!unitMeasuresData) return [];
    return Object.entries(unitMeasuresData)
      .map(([key, value]) => ({
        value: key,
        label: value,
      }))
      .sort((a, b) => {
        if (a.value === 'service') return -1;
        if (b.value === 'service') return 1;
        return 0;
      });
  }, [unitMeasuresData]);

  useEffect(() => {
    if (transformedUnitMeasuresData.length > 0 && !values.unit_measure) {
      setValues((prev) => ({
        ...prev,
        unit_measure: transformedUnitMeasuresData[0].value,
      }));
    }
  }, [transformedUnitMeasuresData]);

  const typesApiPage =
    prevDebouncedTypeSearchRef.current !== debouncedTypeSearch ? 1 : typesPage;

  if (prevDebouncedTypeSearchRef.current !== debouncedTypeSearch) {
    prevDebouncedTypeSearchRef.current = debouncedTypeSearch;
  }

  useEffect(() => {
    setTypesPage(1);
  }, [typeSearchQuery]);

  const {
    data: typesDataRaw,
    isLoading,
    isFetching: isFetchingTypes,
  } = useGetTypesFiltersQuery({
    page: typesApiPage,
    per_page: 20,
    ...(typesDropdownOpen && debouncedTypeSearch ? { search: debouncedTypeSearch } : {}),
  });

  const dataTypes = isTypesOfServicesResponse(typesDataRaw) ? typesDataRaw : undefined;
  const serviceItems =
    dataTypes?.items && isServiceTypeArray(dataTypes.items) ? dataTypes.items : [];
  const hasNextTypesPage =
    !!dataTypes?.meta && dataTypes.meta.current_page < dataTypes.meta.last_page;

  const loadMoreTypes = useCallback(() => {
    if (isFetchingTypes || !hasNextTypesPage || typesLoadMoreLockRef.current) return;
    typesLoadMoreLockRef.current = true;
    setTypesPage((prev) => prev + 1);
  }, [isFetchingTypes, hasNextTypesPage]);

  useEffect(() => {
    if (!isFetchingTypes) {
      typesLoadMoreLockRef.current = false;
    }
  }, [isFetchingTypes]);

  const handleTypesScroll = useCallback(() => {
    const el = typesViewportRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gapToBottom < 72 && el.scrollTop > 12) {
      loadMoreTypes();
    }
  }, [loadMoreTypes]);

  const handleTypesDropdownOpen = useCallback(() => {
    setTypesDropdownOpen(true);
    suppressTypeSearchRef.current = true;
    setTypeSearchInput('');
    setTypeSearchQuery('');
  }, []);

  const handleTypesDropdownClose = useCallback(() => {
    suppressTypeSearchRef.current = true;
    setTypesDropdownOpen(false);
    setTypeSearchInput('');
    setTypeSearchQuery('');
  }, []);

  const handleTypeSearchChange = useCallback(
    (value: string) => {
      const selectedLabel = values.order_type?.trim() ?? '';

      if (!typesDropdownOpen) {
        return;
      }

      if (suppressTypeSearchRef.current) {
        suppressTypeSearchRef.current = false;
        if (!value.trim() || value === selectedLabel) {
          return;
        }
      }

      setTypeSearchInput(value);
      setTypeSearchQuery(value.trim());
      setTypesPage(1);
    },
    [typesDropdownOpen, values.order_type],
  );

  const selectData = useMemo(() => {
    const items = serviceItems.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
    const isTypeSearchActive = typeSearchQuery.trim().length > 0;
    const selectedId = values.order_type_id?.toString();
    if (
      !isTypeSearchActive &&
      selectedId &&
      values.order_type &&
      !items.some((item) => item.value === selectedId)
    ) {
      items.unshift({
        value: selectedId,
        label: values.order_type,
      });
    }
    return items;
  }, [serviceItems, values.order_type_id, values.order_type, typeSearchQuery]);

  useEffect(() => {
    if (serviceItems.length === 0) return;

    if (values.order_type && !values.order_type_id) {
      const matchingType = serviceItems.find((item) => item.name === values.order_type);
      if (matchingType) {
        setValues((prev) => ({ ...prev, order_type_id: matchingType.id }));
      }
    }
  }, [values.order_type, values.order_type_id, serviceItems]);

  const selectedTypeValue =
    values.order_type_id != null && Number(values.order_type_id) > 0
      ? String(values.order_type_id)
      : null;

  const isViewMode = !!currentId;

  useEffect(() => {
    const hasAddressData = !!(
      values.city ||
      values.street ||
      values.home ||
      values.apartment
    );
    if (values.is_remote !== !hasAddressData) {
      setValues((prev) => ({ ...prev, is_remote: !hasAddressData }));
    }
  }, [values.city, values.street, values.home, values.apartment]);

  const descriptionValue = values.description || '';
  const valueCounterError = descriptionValue.length > 3000;

  useEffect(() => {
    const MIN_DESCRIPTION_HEIGHT = 150;

    const measureContentHeight = (textarea: HTMLTextAreaElement) => {
      const prevHeight = textarea.style.getPropertyValue('height');
      const prevPriority = textarea.style.getPropertyPriority('height');

      textarea.style.setProperty('height', '0px', 'important');
      const contentHeight = textarea.scrollHeight;

      if (prevHeight) {
        textarea.style.setProperty('height', prevHeight, prevPriority);
      } else {
        textarea.style.removeProperty('height');
      }

      return contentHeight;
    };

    const updateDescriptionScroll = () => {
      const textarea = descriptionTextareaRef.current;
      if (!textarea) return;

      const contentHeight = measureContentHeight(textarea);
      const needsScroll = contentHeight > MIN_DESCRIPTION_HEIGHT;

      if (document.activeElement === textarea) {
        descriptionSelectionRef.current = {
          start: textarea.selectionStart,
          end: textarea.selectionEnd,
        };
      }
      setScrollHeight(contentHeight);
      setHasDescriptionScroll(needsScroll);
    };

    const frameId = requestAnimationFrame(updateDescriptionScroll);
    window.addEventListener('resize', updateDescriptionScroll);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateDescriptionScroll);
    };
  }, [descriptionValue]);

  useEffect(() => {
    const wrap = descriptionResizeRef.current;
    const textarea = descriptionTextareaRef.current;
    if (!wrap || !textarea) return;

    if (hasDescriptionScroll) {
      const contentHeight = Math.min(500, Math.max(150, scrollHeight));
      const currentHeight = wrap.offsetHeight || contentHeight;
      const height = Math.min(500, Math.max(150, Math.min(currentHeight, contentHeight)));
      wrap.style.height = `${height}px`;
      wrap.style.maxHeight = `${contentHeight}px`;
    } else {
      wrap.style.height = '';
      wrap.style.maxHeight = '';
    }

    const selection = descriptionSelectionRef.current;
    if (selection) {
      descriptionSelectionRef.current = null;
      requestAnimationFrame(() => {
        const el = descriptionTextareaRef.current;
        if (!el) return;
        el.focus({ preventScroll: true });
        el.setSelectionRange(selection.start, selection.end);
      });
    }
  }, [hasDescriptionScroll, scrollHeight]);

  const handleSave = async () => {
    const {
      template_name,
      order_type_id,
      description,
      accept_automatically,
      add_tax_amount,
      date_start,
      date_end,
      is_remote,
      city,
      street,
      home,
      apartment,
      use_fast_withdraw,
      unit_volume,
      unit_volume_min,
      unit_volume_max,
      unit_price,
      unit_measure,
      is_unit_based,
    } = values;

    if (templateNameExists) {
      return;
    }

    if (!template_name.trim() || isLoadingCreate) {
      setError(true);
      return;
    }

    try {
      setError(false);
      setErrorMessage('');
      await createOrderTemplate({
        name: template_name,
        order_type_id: (order_type_id || 0) as string | number,
        description,
        is_remote,
        city,
        street,
        home,
        apartment,
        date_end: date_end ? moment(date_end).format('YYYY-MM-DD 00:00') : null,
        date_start: date_start ? moment(date_start).format('YYYY-MM-DD 00:00') : null,
        use_fast_withdraw,
        unit_volume: +(unit_volume || 1),
        unit_price: normalizeUnitPrice(unit_price),
        unit_measure,
        is_unit_based,
        ...(!is_unit_based && {
          accept_automatically:
            accept_automatically === 1 || accept_automatically === true ? 1 : 0,
          add_tax_amount,
        }),
        ...(is_unit_based && {
          unit_volume_min: +(unit_volume_min || 1),
          unit_volume_max: unit_volume_max ? +unit_volume_max : null,
        }),
      }).unwrap();

      setTemplateName('');
      setValues(getInitialValues(defaultFastWithdraw));
      setOpen(false);
      onSuccess?.('Шаблон успешно сохранен');
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };

      setErrorMessage(
        (err.status !== 500 && err.data?.message) ||
          'При сохранении шаблона произошла ошибка',
      );
      console.log('Ошибка сохранения шаблона', error);
    }
  };

  const handleDelete = async () => {
    await deleteOrderTemplate({ id: currentId?.toString() || '' }).unwrap();
    setOpen(false);
    onSuccess?.('Шаблон успешно удален');
  };

  const handleTogglePercent = () => {
    setValues((prev) => {
      const currentAmount = Number(prev.unit_price ?? 0);
      const unit_volume = Number(prev.unit_volume) || 1;
      const roundedAmount = !prev.add_tax_amount
        ? Math.round(currentAmount * (1 + 0.06 / 0.94) * 100) / 100
        : Math.round((currentAmount / (1 + 0.06 / 0.94)) * 100) / 100;

      return {
        ...prev,
        unit_price: roundedAmount,
        total_price: Math.round(roundedAmount * unit_volume * 100) / 100,
        add_tax_amount: !prev.add_tax_amount,
      };
    });
  };

  const syncUnitVolumeRangeFromMin = useCallback(
    (minValue: string | number = values.unit_volume_min ?? '1') => {
      const max = Number(values.unit_volume_max);
      const min = Number(minValue);

      if (max > 0 && min > max) {
        setValues((prev) => ({ ...prev, unit_volume_max: String(minValue) }));
      }
    },
    [values.unit_volume_max, values.unit_volume_min],
  );

  const syncUnitVolumeRangeFromMax = useCallback(
    (maxValue: string | number = values.unit_volume_max ?? '') => {
      const max = Number(maxValue);
      const min = Number(values.unit_volume_min);

      if (max > 0 && min > max) {
        setValues((prev) => ({ ...prev, unit_volume_min: String(maxValue) }));
      }
    },
    [values.unit_volume_max, values.unit_volume_min],
  );

  if (isLoading) return null;

  const sumWithPercent =
    Number(values.unit_price ?? 0) *
    (infoPercent.commission_percent / 100) *
    Number(values.unit_volume);

  let finalCommission;
  if (values.use_fast_withdraw && values.unit_price) {
    const fastWithdrawRate = (infoPercent.commission_percent + 1) / 100;
    const fastWithdrawFee =
      +values.unit_price * Number(values.unit_volume) * fastWithdrawRate;
    finalCommission = Math.max(fastWithdrawFee, 100);
  } else {
    finalCommission =
      sumWithPercent < infoPercent?.commission_min_amount
        ? infoPercent.commission_min_amount
        : sumWithPercent;
  }

  const baseAmount = Number(values.total_price ?? 0);
  const sumToBePaid = values.total_price ? (baseAmount + finalCommission).toFixed(2) : 0;

  const handleToggleSelling = () => {
    setValues((prev) => {
      if (!prev.add_tax_amount) {
        return {
          ...prev,
          accept_automatically: 0,
          is_unit_based: !prev.is_unit_based,
        };
      }

      const currentAmount = Number(prev.unit_price ?? 0);
      const unit_volume = Number(prev.unit_volume) || 1;
      const roundedAmount = Math.round((currentAmount / (1 + 0.06 / 0.94)) * 100) / 100;

      return {
        ...prev,
        unit_price: roundedAmount,
        total_price: Math.round(roundedAmount * unit_volume * 100) / 100,
        add_tax_amount: false,
        accept_automatically: 0,
        is_unit_based: !prev.is_unit_based,
      };
    });
  };

  return (
    <>
      <Modal
        setOpen={(open) => {
          setOpen(open);
          if (!open) {
            setTemplateName('');
          }
        }}>
        <div className={styles.root}>
          <Title className={styles.root_title} title='Шаблон заказа' />
          <div>
            <Label text='Название (обязательное поле)' />
            <TextInput
              maxLength={30}
              placeholder='Введи название'
              required
              value={values.template_name}
              onChange={(e) => {
                const value = e.target.value;
                setError(false);
                setErrorMessage('');
                setTemplateName(value);
                setValues((prev) => ({ ...prev, template_name: value }));
              }}
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: error || templateNameExists ? '1px solid red' : 'none',
                  backgroundColor: isViewMode ? '#E7ECF4' : 'white',
                },
              }}
              disabled={isViewMode}
            />
            {error && <p className={styles.errorPlaceholder}>Обязательное поле</p>}
            {templateNameExists && (
              <p className={styles.errorPlaceholder}>
                Шаблон с таким названием уже существует
              </p>
            )}
          </div>
          <div>
            <Label text='Услуга' />
            <Select
              data={selectData || []}
              placeholder='Выбери категорию'
              searchable
              searchValue={typesDropdownOpen ? typeSearchInput : undefined}
              onSearchChange={handleTypeSearchChange}
              onDropdownOpen={handleTypesDropdownOpen}
              onDropdownClose={handleTypesDropdownClose}
              filter={({ options }) => options}
              nothingFoundMessage='Ничего не найдено'
              scrollAreaProps={{
                viewportRef: typesViewportRef,
                onScrollPositionChange: handleTypesScroll,
              }}
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: 'none',
                  backgroundColor: isViewMode ? '#E7ECF4' : 'white',
                },
                option: {
                  minHeight: '48px',
                  height: 'auto',
                  fontSize: '16px',
                  padding: '12px 16px',
                  lineHeight: '1.4',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                },
              }}
              value={selectedTypeValue}
              onChange={(val) => {
                if (!val) return;
                const selectedType = selectData.find((item) => item.value === val);
                setValues((prev) => ({
                  ...prev,
                  order_type_id: Number(val),
                  order_type: selectedType ? selectedType.label : '',
                }));
                setTypeSearchInput('');
                setTypeSearchQuery('');
                suppressTypeSearchRef.current = true;
                requestAnimationFrame(() => {
                  (document.activeElement as HTMLElement | null)?.blur?.();
                });
              }}
              disabled={isViewMode}
              withCheckIcon={false}
              rightSection={<ArrowSvg />}
              allowDeselect={false}
              comboboxProps={{
                zIndex: 100001,
                position: 'bottom',
                middlewares: { flip: false, shift: false },
                offset: 4,
              }}
            />
          </div>
          <div>
            <div>
              <Label text='Описание' />
              <div
                ref={descriptionResizeRef}
                className={
                  hasDescriptionScroll
                    ? `${styles.descriptionResize} ${styles.descriptionResize_active}`
                    : styles.descriptionResize
                }>
                <Textarea
                  ref={descriptionTextareaRef}
                  classNames={{
                    root: hasDescriptionScroll
                      ? styles.descriptionTextareaRoot
                      : undefined,
                    input: styles.descriptionTextareaInput,
                  }}
                  placeholder='Введи описание'
                  value={values.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setValues((prev) => ({ ...prev, description: e.target.value }))
                  }
                  disabled={isViewMode}
                  withErrorStyles={false}
                  resize='none'
                  rows={1}
                  autosize={!hasDescriptionScroll}
                  minRows={1}
                  maxRows={5}
                  styles={{
                    input: {
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '12px',
                      paddingBottom: '11px',
                      border: valueCounterError ? '1px solid red' : 'none',
                    },
                    error: { fontSize: '14px', color: '#e10d0d' },
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'end' }}>
              <p
                style={{
                  display: 'flex',
                  alignItems: 'start',
                  color: valueCounterError ? 'red' : '#71869D',
                  paddingTop: '6px',
                }}>
                {descriptionValue.length}/3000
              </p>
            </div>
            <div>
              <Label text='Адрес' />
              <AddressDadata3
                onChange={(addressData) => {
                  setValues((prev) => ({ ...prev, ...addressData }));
                }}
                initialValues={{
                  is_remote: values.is_remote,
                  city: values.city,
                  street: values.street,
                  home: values.home,
                  apartment: values.apartment,
                }}
                disabled={isViewMode}
              />
            </div>
          </div>

          <Group wrap='nowrap'>
            <div style={{ flex: 1 }}>
              <Label text='Дата начала' />
              <DateInput
                value={values.date_start}
                onChange={(value) => {
                  setValues((prev) => ({ ...prev, date_start: value }));
                }}
                disabled={isViewMode}
                placeholder={'Не выбрана'}
                rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
                styles={{
                  input: {
                    height: '48px',
                    fontSize: '16px',
                    border: 'none',
                    backgroundColor: isViewMode ? '#E7ECF4' : 'white',
                  },
                }}
                highlightToday
                minDate={
                  values.date_start ? moment(values.date_start).toDate() : new Date()
                }
                locale={'ru'}
                valueFormat={'DD.MM.YYYY'}
                clearable
                allowDeselect
                popoverProps={{ zIndex: 100001 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label text='Дата завершения' />
              <DateInput
                value={values.date_end}
                onChange={(value) => {
                  setValues((prev) => ({ ...prev, date_end: value }));
                }}
                disabled={isViewMode}
                placeholder={'Не выбрана'}
                rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
                styles={{
                  input: {
                    height: '48px',
                    fontSize: '16px',
                    border: 'none',
                    backgroundColor: isViewMode ? '#E7ECF4' : 'white',
                  },
                }}
                highlightToday
                minDate={
                  values.date_start ? moment(values.date_start).toDate() : new Date()
                }
                locale={'ru'}
                valueFormat={'DD.MM.YYYY'}
                clearable
                allowDeselect
                popoverProps={{ zIndex: 100001 }}
              />
            </div>
          </Group>

          {profileData?.features?.unitbased && (
            <Group gap='8px' h='40px'>
              <Switch
                color='#002CFB'
                styles={{
                  label: {
                    fontSize: '16px',
                    lineHeight: '22px',
                    color: '#121922',
                    cursor: isViewMode ? 'default' : 'pointer',
                  },
                  track: {
                    border: 'none',
                    cursor: isViewMode ? 'not-allowed' : 'pointer',
                    backgroundColor: values.is_unit_based
                      ? '#002CFB'
                      : 'rgba(173, 191, 223, 0.40)',
                  },
                  thumb: {
                    border: 'none',
                  },
                }}
                disabled={isViewMode}
                checked={values.is_unit_based}
                onChange={() => {
                  handleToggleSelling();
                }}
                label='Оплата за выполненный объем'
              />
              <AppTooltip label='Укажи цену за единицу. Итоговая сумма будет рассчитываться при завершении заказа на основе выполненного объема'>
                <InfoIcon style={{ cursor: 'pointer' }} />
              </AppTooltip>
            </Group>
          )}

          <div className={styles.order_inner__input}>
            <div className={styles.amount}>
              <div>
                <Label text='Цена за ед., ₽ ' />
                <NumberInput
                  flex={1}
                  placeholder={'0'}
                  disabled={isViewMode}
                  value={String(values.unit_price || null)}
                  onChange={(val) => {
                    const unit_price = String(val);
                    setValues((prev) => ({
                      ...prev,
                      unit_price,
                      add_tax_amount: false,
                      total_price:
                        val === '' || val === null
                          ? null
                          : Math.round(
                              Number(val) * (Number(prev.unit_volume) || 1) * 100,
                            ) / 100,
                    }));
                  }}
                  allowNegative={false}
                  decimalScale={2}
                  hideControls={true}
                  styles={{
                    input: {
                      height: '46px',
                      fontSize: '16px',
                      border: 'none',
                    },
                    error: { fontSize: '14px', color: '#e10d0d' },
                  }}
                />
              </div>

              <div>
                <Label text='Объем' />
                <NumberInput
                  flex={1}
                  placeholder='1'
                  disabled={isViewMode}
                  allowNegative={false}
                  decimalScale={0}
                  hideControls
                  handlersRef={unitHandlersRef}
                  value={Number(values.unit_volume || 1)}
                  onChange={(val) => {
                    const unitValue = val === '' || val === null ? 1 : Number(val);
                    const unit_volume = Math.max(1, unitValue);
                    setValues((prev) => ({
                      ...prev,
                      unit_volume,
                      total_price:
                        Math.round(Number(prev.unit_price) * unit_volume * 100) / 100,
                    }));
                  }}
                  min={1}
                  startValue={1}
                  rightSectionPointerEvents='auto'
                  rightSection={
                    <QuantityStepperControls
                      onIncrement={() => unitHandlersRef.current?.increment()}
                      onDecrement={() => unitHandlersRef.current?.decrement()}
                      decrementDisabled={Number(values.unit_volume || 1) <= 1}
                      disabled={isViewMode}
                    />
                  }
                  styles={{
                    input: {
                      height: '46px',
                      fontSize: '16px',
                      border: 'none',
                    },
                    error: { fontSize: '14px', color: '#e10d0d' },
                  }}
                />
              </div>

              <div>
                <Label text='Ед. изм-я' />
                {isLoadingUnitMeasures ? (
                  <ContentLoader
                    speed={2}
                    width={'100%'}
                    height={'48px'}
                    backgroundColor={'#FFFFFF'}
                    foregroundColor={'#D8E4FB8C'}>
                    <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                  </ContentLoader>
                ) : (
                  <Select
                    data={transformedUnitMeasuresData}
                    nothingFoundMessage='Ничего не найдено'
                    maxDropdownHeight={160}
                    disabled={isViewMode}
                    styles={{
                      input: {
                        height: '46px',
                        fontSize: '16px',
                        border: 'none',
                        cursor: isViewMode ? 'not-allowed' : 'pointer',
                      },
                      option: {
                        minHeight: '46px',
                        height: 'auto',
                        fontSize: '16px',
                        padding: '12px 16px',
                        lineHeight: '1.4',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                      },
                    }}
                    value={
                      transformedUnitMeasuresData?.find(
                        (item) =>
                          item.label === values.unit_measure ||
                          item.value === values.unit_measure?.toString(),
                      )?.value ??
                      transformedUnitMeasuresData[0]?.value ??
                      null
                    }
                    onChange={(val) => {
                      const selected = transformedUnitMeasuresData?.find(
                        (item) => item.value === val,
                      );
                      setValues((prev) => ({
                        ...prev,
                        unit_measure: selected?.value ?? '',
                      }));
                    }}
                    withCheckIcon={false}
                    rightSection={<ArrowSvg />}
                    allowDeselect={false}
                    comboboxProps={{
                      zIndex: 100001,
                      position: 'bottom',
                      middlewares: { flip: false, shift: false },
                      offset: 4,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {!values.is_unit_based ? (
            <>
              <Group gap='8px'>
                <Switch
                  color='#002CFB'
                  styles={{
                    label: {
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#121922',
                      cursor: isViewMode ? 'default' : 'pointer',
                    },
                    track: {
                      border: 'none',
                      cursor: isViewMode ? 'not-allowed' : 'pointer',
                      backgroundColor: values.add_tax_amount
                        ? '#002CFB'
                        : 'rgba(173, 191, 223, 0.40)',
                    },
                    thumb: {
                      border: 'none',
                    },
                  }}
                  checked={values.add_tax_amount}
                  onChange={() => {
                    handleTogglePercent();
                  }}
                  disabled={isViewMode}
                  label='Добавить 6%'
                />
                <AppTooltip
                  label={
                    values.total_price
                      ? `Мы добавим такую сумму, которая после выплаты 6% налога, составит ${values.total_price} ₽`
                      : `Мы добавим такую сумму, которая после выплаты 6% налога составит введенную`
                  }>
                  <InfoIcon style={{ cursor: 'pointer' }} />
                </AppTooltip>
              </Group>
              <Group gap='8px' mt='4px' style={{ cursor: 'pointer' }}>
                <Switch
                  color='#002CFB'
                  styles={{
                    label: {
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#121922',
                      cursor: isViewMode ? 'default' : 'pointer',
                    },
                    track: {
                      border: 'none',
                      cursor: isViewMode ? 'not-allowed' : 'pointer',
                      backgroundColor:
                        values.accept_automatically === 1 ||
                        values.accept_automatically === true
                          ? '#002CFB'
                          : 'rgba(173, 191, 223, 0.40)',
                    },
                    thumb: {
                      border: 'none',
                    },
                  }}
                  checked={Boolean(values.accept_automatically)}
                  onChange={(event) => {
                    setValues((prev) => ({
                      ...prev,
                      accept_automatically: event.currentTarget.checked ? 1 : 0,
                    }));
                  }}
                  disabled={isViewMode}
                  label='Завершить заказ автоматически'
                />
                <AppTooltip
                  label={
                    'После завершения заказа исполнителем автоматически будет подписан акт и отправлена выплата'
                  }>
                  <InfoIcon style={{ cursor: 'pointer' }} />
                </AppTooltip>
              </Group>
              <div>
                <div className={styles.total_amount}>
                  <p>{addSpaceNumber(sumToBePaid || 0)} ₽</p>
                  <AppTooltip
                    label={`Цена указана с учетом комиссии компании ${infoPercent.commission_percent}%`}>
                    <InfoIcon style={{ cursor: 'pointer' }} />
                  </AppTooltip>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.amount_minmax}>
                <div>
                  <p className={styles.volumeLabel}>
                    <span className={styles.volumeLabel_full}>Минимальный объем</span>
                    <span className={styles.volumeLabel_short}>Мин. объем</span>
                  </p>
                  <NumberInput
                    flex={1}
                    placeholder='1'
                    disabled={isViewMode}
                    allowNegative={false}
                    decimalScale={0}
                    hideControls
                    value={values.unit_volume_min || '1'}
                    onChange={(val) => {
                      setValues((prev) => ({ ...prev, unit_volume_min: String(val) }));
                    }}
                    onBlur={() => syncUnitVolumeRangeFromMin()}
                    min={1}
                    rightSectionPointerEvents='auto'
                    rightSection={
                      <QuantityStepperControls
                        onIncrement={() => {
                          const newMin = Number(values.unit_volume_min || 1) + 1;
                          setValues((prev) => ({
                            ...prev,
                            unit_volume_min: String(newMin),
                          }));
                          syncUnitVolumeRangeFromMin(newMin);
                        }}
                        onDecrement={() => {
                          const current = Number(values.unit_volume_min || 1);
                          if (current <= 1) return;
                          const newMin = current - 1;
                          setValues((prev) => ({
                            ...prev,
                            unit_volume_min: String(newMin),
                          }));
                          syncUnitVolumeRangeFromMin(newMin);
                        }}
                        decrementDisabled={Number(values.unit_volume_min || '1') <= 1}
                        disabled={isViewMode}
                      />
                    }
                    styles={{
                      input: {
                        height: '46px',
                        fontSize: '16px',
                        border: 'none',
                      },
                      error: { fontSize: '14px', color: '#e10d0d' },
                    }}
                  />
                </div>
                <div>
                  <p className={styles.volumeLabel}>
                    <span className={styles.volumeLabel_full}>Максимальный объем</span>
                    <span className={styles.volumeLabel_short}>Макс. объем</span>
                  </p>
                  <NumberInput
                    flex={1}
                    placeholder='0'
                    allowNegative={false}
                    decimalScale={1}
                    disabled={isViewMode}
                    hideControls
                    value={values.unit_volume_max || ''}
                    onChange={(val) => {
                      setValues((prev) => ({ ...prev, unit_volume_max: String(val) }));
                    }}
                    onBlur={() => syncUnitVolumeRangeFromMax()}
                    min={0}
                    rightSectionPointerEvents='auto'
                    rightSection={
                      <QuantityStepperControls
                        disabled={isViewMode}
                        onIncrement={() => {
                          const newMax =
                            values.unit_volume_max === ''
                              ? 1
                              : Number(values.unit_volume_max) + 1;
                          setValues((prev) => ({
                            ...prev,
                            unit_volume_max: String(newMax),
                          }));
                          syncUnitVolumeRangeFromMax(newMax);
                        }}
                        onDecrement={() => {
                          if (values.unit_volume_max === '') return;

                          const current = Number(values.unit_volume_max);
                          if (current <= 0) return;

                          if (current === 1) {
                            setValues((prev) => ({ ...prev, unit_volume_max: '' }));
                            return;
                          }

                          const newMax = current - 1;
                          setValues((prev) => ({
                            ...prev,
                            unit_volume_max: String(newMax),
                          }));
                          syncUnitVolumeRangeFromMax(newMax);
                        }}
                        decrementDisabled={
                          !values.unit_volume_max || Number(values.unit_volume_max) <= 0
                        }
                      />
                    }
                    styles={{
                      input: {
                        height: '46px',
                        fontSize: '16px',
                        border: 'none',
                      },
                      error: { fontSize: '14px', color: '#e10d0d' },
                    }}
                  />
                </div>
              </div>

              <div className={styles.total_amount}>
                <p>
                  {addSpaceNumber(Number(values.unit_price) || 0)} ₽/
                  {transformedUnitMeasuresData?.find(
                    (item) =>
                      item.label === values.unit_measure ||
                      item.value === values.unit_measure?.toString(),
                  )?.label ??
                    transformedUnitMeasuresData[0]?.label ??
                    null}
                </p>
              </div>
            </>
          )}

          <Stack gap='8px'>
            {!currentId ? (
              <Button
                theme='filed'
                className={styles.button}
                size='L'
                onClick={handleSave}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {'Сохранить'}
                  {isLoadingCreate ? <Loader size='xs' color='white' /> : <IconDone />}
                </div>
              </Button>
            ) : (
              <Button
                icon
                size='L'
                src={iconDelete}
                className={styles.button_delete}
                theme='link'
                disabled={isDeleting}
                onClick={() => setShowConfirmModal(true)}>
                Удалить
              </Button>
            )}
          </Stack>
          {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        </div>
      </Modal>
      {showConfirmModal && (
        <ModalConfirmTemplate
          handleDelete={handleDelete}
          setShowConfirmModal={setShowConfirmModal}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};
